import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RADIO_PLAN_DESC } from '@app/core/models/Radio';
import { LogNav, SetEventProperties } from '@app/core/states/events-tracking.actions';
import { DisplayUpgradeDiscoverModal } from '@app/core/states/live-tracking.actions';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { LibraryBacHelperService } from '@app/library/services/library-bac-helper.service';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { CloseBac, OpenBac, ToggleBac } from '@app/library/states/bacs.actions';
import { BacsState } from '@app/library/states/bacs.state';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { DndDropEvent } from '@app/shared/modules/dnd/directives/dnd-dropzone.directive';
import { formatTrackingNavName, planBigger } from '@app/shared/utils';
import { Select, Store } from '@ngxs/store';
import { slideInOutBlock } from '@radioking/ui/animations';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { RootBac } from '../../../models/bac.model';

@Component({
  selector: 'rk-library-root-bac',
  templateUrl: './library-root-bac.component.html',
  styleUrls: ['./library-root-bac.component.scss'],
  animations: [slideInOutBlock],
})
export class LibraryRootBacComponent implements OnInit, OnDestroy {
  @Input()
  bac: RootBac;

  @HostBinding('class.open')
  public isOpen: boolean;

  @HostBinding('class.active')
  public isActive: boolean;

  @HostBinding('class.has-child-open')
  public hasChildOpen = false;

  @HostBinding('class.no-bar')
  @Input()
  disableBar: boolean;

  @Input()
  radioId: number;

  @Input()
  planDesc: RADIO_PLAN_DESC;

  @Select(SelectedTracksState.selectedTracks)
  selectedTracks$: Observable<number[]>;
  subscription = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly uiHelper: LibraryBacHelperService,
    private readonly modalService: LibraryModalService,
  ) {}

  ngOnInit() {
    this.isOpen = false;
    // Listen to store bac openings
    this.subscription.add(
      this.store
        .select(BacsState.isBacOpen)
        .pipe(map(fn => fn(this.bac.id)))
        .subscribe(val => {
          this.isOpen = val;
          if (this.isOpen && (!this.bac.children || this.bac.children.length === 0)) {
            this.store.dispatch(new CloseBac(this.bac.id));
          }
        }),
    );

    // Listen for route changes
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          if (!event.urlAfterRedirects) {
            return;
          }
          this.checkForClasses(event.urlAfterRedirects);
        }
      }),
    );
    this.subscription.add(
      this.route.url.subscribe(() => {
        this.checkForClasses(this.router.url);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  clickMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  toggleChildren() {
    this.store.dispatch(new ToggleBac(this.bac.id));
  }

  checkForClasses(url: string): void {
    // set the active route or not
    this.isActive = this.uiHelper.isBacActive(url, this.bac);

    // Check if the url can be found in
    // one of the children of this item
    this.hasChildOpen = this.uiHelper.isUrlInChildren(this.bac, url);

    if (this.hasChildOpen) {
      this.store.dispatch(new OpenBac(this.bac.id));
    }
  }

  dropped(event: DndDropEvent) {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (selectedTracks.indexOf(event.data.id) > -1 && selectedTracks.length > 1) {
      this.modalService.moveMultipleTrack(selectedTracks, this.bac);
    } else {
      this.modalService.moveTrack(event.data, this.bac);
    }
  }

  clickBac() {
    this.trackClickNavigation();
    if (
      this.bac.name === '__DEDICATION__' &&
      planBigger('Start', this.store.selectSnapshot(LiveTrackingState.planDesc))
    ) {
      this.store.dispatch(
        new DisplayUpgradeDiscoverModal({ origin: 'library_audio_shoutouts' }),
      );
    }
  }

  trackClickNavigation() {
    const removeChar = /_/gi;
    const trackingBoxName = this.bac.name.replace(removeChar, '').toLowerCase();
    this.store.dispatch(
      new SetEventProperties({
        name: formatTrackingNavName('subnav-library-' + trackingBoxName),
        status: this.bacPlanLocked() ? 'Inactive' : 'Active',
      }),
    );
    this.store.dispatch(new LogNav(AMPLITUDE_EVENT.NAV.CLICK_SUBNAV));
  }

  bacPlanLocked() {
    return this.bac.name === '__DEDICATION__' && planBigger('Start', this.planDesc);
  }

  planBigger(plan: RADIO_PLAN_DESC, compareTo: RADIO_PLAN_DESC) {
    return planBigger(plan, compareTo);
  }
}
